import { type FC, useMemo } from "react";

import type {
  PlanSnapshot,
  PlanSnapshotAttachment,
  PlanSnapshotDependency,
  PlanSnapshotNodeWithDependencyCounts,
} from "../../types";

import { formatDateTime } from "../../utils/time";
import {
  AccentCard,
  CARD_META_CLASS,
  StatusDot,
  Text,
  getStatusAccent,
  getStatusDot,
  isActiveStatus,
} from "../common";
import { SideSheet } from "../common/SideSheet";
import { PlanSnapshotDrawerAttachmentList } from "./PlanSnapshotDrawerAttachmentList";
import { PlanSnapshotDrawerDependencyList } from "./PlanSnapshotDrawerDependencyList";
import { PlanSnapshotDrawerDependentsList } from "./PlanSnapshotDrawerDependentsList";

function riskClassName(risk: string): string {
  switch (risk.toLowerCase()) {
    case "high": {
      return "text-rose-500 dark:text-rose-400";
    }
    case "medium": {
      return "text-amber-500 dark:text-amber-400";
    }
    default: {
      return "text-emerald-500 dark:text-emerald-400";
    }
  }
}

function resolveDependency(
  dep: PlanSnapshotDependency,
  nodeById: Map<string, PlanSnapshotNodeWithDependencyCounts>,
  nodeByIssueKey: Map<string, PlanSnapshotNodeWithDependencyCounts>,
): PlanSnapshotNodeWithDependencyCounts | null {
  if (dep.depends_on_node_id) {
    const direct = nodeById.get(dep.depends_on_node_id);
    if (direct) {
      return direct;
    }
  }
  return nodeByIssueKey.get(dep.depends_on_key) ?? null;
}

export interface PlanSnapshotDrawerProps {
  open: boolean;
  node: PlanSnapshotNodeWithDependencyCounts | null;
  snapshot: PlanSnapshot | null;
  nodeById: Map<string, PlanSnapshotNodeWithDependencyCounts>;
  nodeByIssueKey: Map<string, PlanSnapshotNodeWithDependencyCounts>;
  dependentsByNodeId: Map<string, PlanSnapshotNodeWithDependencyCounts[]>;
  selectedAttachment: string | null;
  onClose: () => void;
  onSelectNode: (nodeId: string) => void;
  onSelectAttachment: (filename: string | null) => void;
}

export const PlanSnapshotDrawer: FC<PlanSnapshotDrawerProps> = ({
  open,
  node,
  snapshot,
  nodeById,
  nodeByIssueKey,
  dependentsByNodeId,
  selectedAttachment,
  onClose,
  onSelectNode,
  onSelectAttachment,
}) => {
  const attachmentsByFilename = useMemo(() => {
    if (!snapshot?.attachments?.length) {
      return new Map<string, PlanSnapshotAttachment>();
    }
    return new Map(
      snapshot.attachments.map((attachment) => [
        attachment.filename,
        attachment,
      ]),
    );
  }, [snapshot]);

  const attachmentRefs = useMemo(
    () => node?.attachment_refs?.filter(Boolean) ?? [],
    [node],
  );

  const resolvedAttachments = useMemo(
    () =>
      attachmentRefs
        .map((filename) => ({
          attachment: attachmentsByFilename.get(filename) ?? null,
          filename,
        }))
        .sort((a, b) => a.filename.localeCompare(b.filename)),
    [attachmentRefs, attachmentsByFilename],
  );

  const resolvedDependencies = useMemo(() => {
    if (!node) {
      return [];
    }
    return node.dependencies
      .map((dep) => ({
        dep,
        resolved: resolveDependency(dep, nodeById, nodeByIssueKey),
      }))
      .toSorted((a, b) =>
        a.dep.depends_on_key.localeCompare(b.dep.depends_on_key),
      );
  }, [node, nodeById, nodeByIssueKey]);

  const dependents = useMemo(() => {
    if (!node) {
      return [];
    }
    return [...(dependentsByNodeId.get(node.node_id) ?? [])].toSorted((a, b) =>
      a.issue_key.localeCompare(b.issue_key),
    );
  }, [dependentsByNodeId, node]);

  if (!node) {
    return null;
  }

  return (
    <SideSheet
      isOpen={open}
      onClose={onClose}
      title={node.issue_key}
      widthClass="w-full max-w-xl"
      subtitle={
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
          <span className="rounded-none border border-slate-300 bg-slate-100 px-1.5 py-px text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
            {node.classification || "uncategorized"}
          </span>
          {node.status && (
            <span className="rounded-none border border-slate-300 bg-slate-100 px-1.5 py-px text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
              {node.status}
            </span>
          )}
          {snapshot && (
            <span className="text-gray-400 dark:text-gray-500">
              v{snapshot.version}
            </span>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {/* Main info card */}
        <AccentCard
          accent={getStatusAccent(node.status)}
          className="px-3 py-2.5"
        >
          {/* Title + status */}
          <div className="flex items-center gap-1.5">
            <StatusDot
              color={getStatusDot(node.status)}
              pulse={isActiveStatus(node.status)}
            />
            <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">
              {node.issue_key}
            </span>
            {node.is_story && (
              <Text
                variant="label"
                className="rounded-none border border-emerald-300 bg-emerald-100 px-1.5 py-px text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
              >
                Story
              </Text>
            )}
          </div>

          {/* Description */}
          <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
            {node.title ??
              node.humanized_description ??
              node.description ??
              "Untitled task"}
          </p>

          {/* Counts row */}
          <div
            className={`mt-2 flex flex-wrap items-center gap-2 ${CARD_META_CLASS}`}
          >
            <span>{node.depends_on_count} dependencies</span>
            <span>{node.dependents_count} dependents</span>
            {node.unresolved_dependency_count > 0 && (
              <span className="text-amber-500 dark:text-amber-400">
                {node.unresolved_dependency_count} unresolved
              </span>
            )}
            {node.self_assessed_risk && (
              <span className={riskClassName(node.self_assessed_risk)}>
                risk: {node.self_assessed_risk}
              </span>
            )}
          </div>

          {/* Metadata grid */}
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-gray-100 pt-2 dark:border-white/5">
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Node ID
              </dt>
              <dd className="mt-0.5 break-all font-mono text-xs text-gray-600 dark:text-gray-400">
                {node.node_id}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Snapshot ID
              </dt>
              <dd className="mt-0.5 break-all font-mono text-xs text-gray-600 dark:text-gray-400">
                {node.snapshot_id}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Updated
              </dt>
              <dd className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                {formatDateTime(node.updated_at)}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Repository
              </dt>
              <dd className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                {node.repository_slug ?? "\u2014"}
              </dd>
            </div>
          </dl>
        </AccentCard>

        <PlanSnapshotDrawerDependencyList
          items={resolvedDependencies}
          onSelectNode={onSelectNode}
        />
        <PlanSnapshotDrawerDependentsList
          dependents={dependents}
          onSelectNode={onSelectNode}
        />
        <PlanSnapshotDrawerAttachmentList
          items={resolvedAttachments}
          selectedAttachment={selectedAttachment}
          onSelectAttachment={onSelectAttachment}
        />
      </div>
    </SideSheet>
  );
};
